// Shared engine for the "how it works" explainers.
// Every scene is a pure function of `t` (ms into its loop). The clock only ticks while the
// explainer is on screen and the tab is visible; CSS transitions do the actual motion.
// Under prefers-reduced-motion (or autoPlay=false) the scene renders its end frame statically.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { resolveUnit, type ExplainerUnit, type Unit } from './unit';
export type { ExplainerUnit, Unit } from './unit';

export type Tone = 'light' | 'dark';
export type ExplainerProps = { tone?: Tone; autoPlay?: boolean; className?: string; /** The unit the story is about (defaults to the agri baler). */ unit?: ExplainerUnit };
export type SceneProps = { t: number; tone: Tone; u: Unit };
export type SceneDef = {
  name: string;
  /** Accessible description of the whole micro-story. */
  label: string | ((u: Unit) => string);
  W: number;
  H: number;
  duration: number;
  /** The frame shown statically under reduced motion. */
  end: number;
  steps: { at: number; label: string }[];
  Scene: (p: SceneProps) => React.ReactElement;
};

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
export const EASE = 'cubic-bezier(0.215, 0.61, 0.355, 1)'; // power3.out

/* ------------------------------------------------------------------ styles */
const CSS = `
.xp{--xe:${EASE};position:relative;color:var(--xp-fg);width:100%;overflow:hidden;border-radius:18px;font-family:Poppins,Arial,sans-serif;-webkit-font-smoothing:antialiased;line-height:1.3;letter-spacing:-.006em;isolation:isolate;text-align:left}
.xp--light{--xp-fg:#241E61;--xp-muted:#67648F;--xp-line:#E4E3F0;--xp-accent:#2F9E58;--xp-soft:rgba(36,30,97,.05);background:#F7F7FC;box-shadow:inset 0 0 0 1px #E4E3F0}
.xp--dark{--xp-fg:#fff;--xp-muted:rgba(255,255,255,.62);--xp-line:rgba(255,255,255,.14);--xp-accent:#CFF710;--xp-soft:rgba(255,255,255,.06);background:rgba(255,255,255,.04);box-shadow:inset 0 0 0 1px rgba(255,255,255,.12)}
.xp-inner{position:absolute;left:0;top:0;transform-origin:0 0;transition:opacity .32s ease}
.xp-snap *{transition:none!important}
.xp-a{transition:transform .56s var(--xe),opacity .22s var(--xe)}
.xp-f{transition:opacity .2s var(--xe)}
.xp-mono{font-family:'Geist Mono',ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:.06em}
.xp-cap{font-family:'Geist Mono',ui-monospace,SFMono-Regular,Menlo,monospace;font-size:10px;letter-spacing:.12em;text-transform:uppercase;font-weight:500}
.xp-card{background:#fff;border-radius:12px;color:#241E61;box-shadow:0 0 0 1px #E4E3F0,0 18px 36px -22px rgba(24,19,67,.4)}
.xp--dark .xp-card{box-shadow:0 0 0 1px rgba(255,255,255,.06),0 26px 50px -24px rgba(0,0,0,.7)}
.xp-abs{position:absolute}
.xp-row{display:flex;align-items:center}
.xp-tick path{stroke-dasharray:14;stroke-dashoffset:14;transition:stroke-dashoffset .3s var(--xe) .08s}
.xp-tick.on path{stroke-dashoffset:0}
.xp-fig{margin:0}
@keyframes xp-pulse{0%{transform:scale(.6);opacity:.55}100%{transform:scale(2.2);opacity:0}}
@keyframes xp-blink{0%,100%{opacity:.25}50%{opacity:1}}
@media (prefers-reduced-motion: reduce){.xp *,.xp-fig *{transition:none!important;animation:none!important}}
`;
if (typeof document !== 'undefined') {
  let el = document.getElementById('xp-styles') as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = 'xp-styles';
    document.head.appendChild(el);
  }
  el.textContent = CSS;
}

/* ------------------------------------------------------------------ clock */
/** Review hook: the gallery can freeze every explainer at one moment (ms) for screenshots. */
export const FreezeContext = React.createContext<number | undefined>(undefined);

function useReducedMotion() {
  const q = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-reduced-motion: reduce)') : undefined;
  const [r, setR] = useState(!!q?.matches);
  useEffect(() => {
    if (!q) return;
    const on = () => setR(q.matches);
    q.addEventListener?.('change', on);
    return () => q.removeEventListener?.('change', on);
  }, [q]);
  return r;
}

/** Visible = on screen now (not just once) and the tab is not hidden. */
export function useOnScreen<T extends HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [onScreen, setOnScreen] = useState(false);
  const [pageVisible, setPageVisible] = useState(typeof document === 'undefined' ? true : !document.hidden);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setOnScreen(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  useEffect(() => {
    const on = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return [ref, onScreen && pageVisible] as const;
}

/**
 * A pausable clock. `running` gates ticking. With `loop` it wraps to 0; otherwise it stops at
 * `duration` and calls onEnd once. `staticAt` (reduced motion / no autoplay) freezes on that frame.
 */
export function useClock({ duration, running, loop = true, onEnd, staticAt }: { duration: number; running: boolean; loop?: boolean; onEnd?: () => void; staticAt?: number }) {
  const [t, setT] = useState(staticAt ?? 0);
  const tRef = useRef(t);
  const endRef = useRef(onEnd);
  endRef.current = onEnd;
  const frozen = staticAt !== undefined;
  useEffect(() => {
    if (frozen) {
      tRef.current = staticAt!;
      setT(staticAt!);
    }
  }, [frozen, staticAt]);
  useEffect(() => {
    if (!running || frozen) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const now = performance.now();
      const dt = Math.min(now - last, 120);
      last = now;
      if (!loop && tRef.current >= duration) return;
      let nt = tRef.current + dt;
      if (nt >= duration && !loop) {
        tRef.current = duration;
        setT(duration);
        endRef.current?.(); // may seek(0) to restart
        return;
      }
      if (nt >= duration) nt = 0;
      tRef.current = nt;
      setT(nt);
    }, 50);
    return () => window.clearInterval(id);
  }, [running, frozen, duration, loop]);
  const seek = useCallback((v: number) => {
    tRef.current = v;
    setT(v);
  }, []);
  return { t, seek };
}

/* ------------------------------------------------------------------ stage */
/** Fixed-coordinate stage (W x H) scaled to the container width, so layouts stay exact at 320 to 640px. */
export const labelOf = (def: SceneDef, u: Unit) => (typeof def.label === 'function' ? def.label(u) : def.label);

export function Stage({ W, H, tone, t, duration, loop = true, label, children }: { W: number; H: number; tone: Tone; t: number; duration: number; loop?: boolean; label: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [s, setS] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setS(el.clientWidth / W || 1);
    measure();
    if (!('ResizeObserver' in window)) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [W]);
  const exiting = loop && t > duration - 380;
  const snap = t < 90;
  return (
    <div ref={ref} className={cx('xp', `xp--${tone}`)} style={{ aspectRatio: `${W} / ${H}` }} role="img" aria-label={label}>
      <div className={cx('xp-inner', snap && 'xp-snap')} style={{ width: W, height: H, transform: `scale(${s})`, opacity: exiting ? 0 : 1 }} aria-hidden="true">
        {children}
      </div>
    </div>
  );
}

/** Standard looping explainer: the stage alone, starts on scroll-in, pauses off-screen. */
export function Explainer({ def, tone = 'light', autoPlay = true, className, unit }: ExplainerProps & { def: SceneDef }) {
  const reduced = useReducedMotion();
  const [ref, visible] = useOnScreen<HTMLElement>(0.25);
  const freeze = React.useContext(FreezeContext);
  const still = reduced || !autoPlay || freeze !== undefined;
  const { t } = useClock({ duration: def.duration, running: visible, staticAt: still ? (freeze !== undefined ? Math.min(freeze, def.end) : def.end) : undefined });
  const u = useMemo(() => resolveUnit(unit), [unit]);
  const S = def.Scene;
  return (
    <figure ref={ref} className={cx('xp-fig', className)}>
      <Stage W={def.W} H={def.H} tone={tone} t={t} duration={def.duration} loop={!still} label={labelOf(def, u)}>
        <S t={t} tone={tone} u={u} />
      </Stage>
    </figure>
  );
}

/* ------------------------------------------------------------------ helpers */
/** Arrival style: rises 8px and appears. Use with className "xp-a". */
export const rise = (on: boolean, delay = 0, dy = 8): React.CSSProperties => ({
  opacity: on ? 1 : 0,
  transform: on ? 'none' : `translateY(${dy}px)`,
  transitionDelay: on ? `${delay}ms` : '0ms',
});

/** Animated check mark (stroke draw). */
export function Tick({ on, size = 14, color = '#fff' }: { on: boolean; size?: number; color?: string }) {
  return (
    <svg className={cx('xp-tick', on && 'on')} width={size} height={size} viewBox="0 0 14 14" fill="none">
      <path d="M3 7.4 5.8 10 11 4.2" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Deterministic QR-like code (21 modules, three finder patterns). Decorative. */
export function Qr({ size = 64, seed = 7, color = '#241E61', bg = '#fff' }: { size?: number; seed?: number; color?: string; bg?: string }) {
  const d = React.useMemo(() => {
    const n = 21;
    let x = seed * 9301 + 49297;
    const rnd = () => ((x = (x * 9301 + 49297) % 233280) / 233280);
    const finder = (r: number, c: number) => (r < 8 && c < 8) || (r < 8 && c > n - 9) || (r > n - 9 && c < 8);
    let p = '';
    for (let r = 0; r < n; r++)
      for (let c = 0; c < n; c++) {
        if (finder(r, c)) continue;
        if (rnd() > 0.52) p += `M${c} ${r}h1v1h-1z`;
      }
    const fp = (r: number, c: number) => `M${c} ${r}h7v7h-7zM${c + 1} ${r + 1}v5h5v-5zM${c + 2} ${r + 2}h3v3h-3z`;
    return p + fp(0, 0) + fp(0, n - 7) + fp(n - 7, 0);
  }, [seed]);
  return (
    <svg width={size} height={size} viewBox="-1 -1 23 23" shapeRendering="crispEdges">
      <rect x="-1" y="-1" width="23" height="23" fill={bg} />
      <path d={d} fill={color} fillRule="evenodd" />
    </svg>
  );
}

/** Phone body drawn at stage scale. Bleeds off the stage bottom when `crop` is set by the caller. */
export function PhoneFrame({ x, y, w, h, tone, children, style }: { x: number; y: number; w: number; h: number; tone: Tone; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      className="xp-abs"
      style={{
        left: x,
        top: y,
        width: w,
        height: h,
        borderRadius: 34,
        padding: 7,
        background: tone === 'dark' ? '#0E0B2B' : '#181343',
        boxShadow: tone === 'dark' ? '0 0 0 1px rgba(255,255,255,.14), 0 30px 60px -20px rgba(0,0,0,.6)' : '0 30px 60px -28px rgba(24,19,67,.6)',
        ...style,
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 27, overflow: 'hidden', background: '#fff', color: '#241E61' }}>
        <div className="xp-abs" style={{ left: '50%', top: 7, width: 64, height: 18, marginLeft: -32, borderRadius: 18, background: '#181343', zIndex: 20 }} />
        {children}
      </div>
    </div>
  );
}

/** Tap ripple at a point (shown while `on`). */
export function Tap({ x, y, on }: { x: number; y: number; on: boolean }) {
  if (!on) return null;
  return (
    <span className="xp-abs" style={{ left: x - 14, top: y - 14, width: 28, height: 28, pointerEvents: 'none', zIndex: 30 }}>
      <span className="xp-abs" style={{ inset: 0, borderRadius: 99, background: 'rgba(63,174,108,.45)', animation: `xp-pulse .7s ${EASE} forwards` }} />
      <span className="xp-abs" style={{ left: 9, top: 9, width: 10, height: 10, borderRadius: 99, background: 'rgba(36,30,97,.35)' }} />
    </span>
  );
}

export const C = {
  indigo: '#241E61',
  deep: '#181343',
  ink: '#232047',
  muted: '#67648F',
  line: '#E4E3F0',
  alt: '#F7F7FC',
  green: '#3FAE6C',
  mid: '#8FD53A',
  lime: '#CFF710',
  emerald: '#2F9E58',
  grad: 'linear-gradient(95deg, #3FAE6C 0%, #8FD53A 55%, #CFF710 100%)',
};
